<script setup>
/**
 * This component handles parsing and sending WhatsApp message templates.
 * It works as follows:
 * 1. Displays the template text with variable placeholders.
 * 2. Generates input fields for each variable in the template.
 * 3. Validates that all variables are filled before sending.
 * 4. Replaces placeholders with user-provided values.
 * 5. Emits events to send the processed message or reset the template.
 */
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { requiredIf } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';

import { isWhatsAppComplete } from '@chatwoot/utils';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TemplateHeaderMediaAPI from 'dashboard/api/templateHeaderMedia';
import {
  buildTemplateParameters,
  applyAutomaticTemplateParameters,
  allKeysRequired,
  DEFAULT_LANGUAGE,
  DEFAULT_CATEGORY,
  COMPONENT_TYPES,
  MEDIA_FORMATS,
  findComponentByType,
  renderTemplatePreview,
} from 'dashboard/helper/templateHelper';
import { isTemplateTypeSendable } from 'dashboard/helper/whatsappTemplateTypes';

const props = defineProps({
  template: {
    type: Object,
    default: () => ({}),
    validator: value => {
      if (!value || typeof value !== 'object') return false;
      if (!value.components || !Array.isArray(value.components)) return false;
      return true;
    },
  },
  systemVariables: {
    type: Array,
    default: () => [],
  },
  // Caixas que não renderizam o modelo no servidor (API) precisam receber o
  // texto já com as variáveis aplicadas.
  sendRenderedContent: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['sendMessage', 'resetTemplate', 'back']);

const { t } = useI18n();

const processedParams = ref({});
const openVarMenu = ref(null);
const mediaFileInput = ref(null);
const mediaUploading = ref(false);
const mediaUploadError = ref('');
const reusedMediaFileName = ref('');
let mediaUploadRequestId = 0;
let savedMediaRequestId = 0;

const MEDIA_UPLOAD_ACCEPT = {
  IMAGE: 'image/jpeg,image/png',
  VIDEO: 'video/mp4',
  DOCUMENT: 'application/pdf',
};

const toggleVarMenu = key => {
  openVarMenu.value = openVarMenu.value === key ? null : key;
};

const insertSystemVariable = (key, value) => {
  processedParams.value.body[key] = value;
  openVarMenu.value = null;
};

const closeVarMenus = () => {
  openVarMenu.value = null;
};

const languageLabel = computed(() => {
  return `${t('WHATSAPP_TEMPLATES.PARSER.LANGUAGE')}: ${props.template.language || DEFAULT_LANGUAGE}`;
});

const categoryLabel = computed(() => {
  return `${t('WHATSAPP_TEMPLATES.PARSER.CATEGORY')}: ${props.template.category || DEFAULT_CATEGORY}`;
});

const headerComponent = computed(() => {
  return findComponentByType(props.template, COMPONENT_TYPES.HEADER);
});

const bodyComponent = computed(() => {
  return findComponentByType(props.template, COMPONENT_TYPES.BODY);
});

const bodyText = computed(() => {
  return bodyComponent.value?.text || '';
});

const headerText = computed(() => {
  return headerComponent.value?.format === 'TEXT'
    ? headerComponent.value?.text || ''
    : '';
});

const hasMediaHeader = computed(() =>
  MEDIA_FORMATS.includes(headerComponent.value?.format)
);

const formatType = computed(() => {
  const format = headerComponent.value?.format;
  return format ? format.charAt(0) + format.slice(1).toLowerCase() : '';
});

const isDocumentTemplate = computed(() => {
  return headerComponent.value?.format?.toLowerCase() === 'document';
});

const hasBodyVariables = computed(() => {
  return bodyText.value?.match(/{{([^}]+)}}/g) !== null;
});

const hasTextHeaderVariables = computed(() => {
  return headerText.value?.match(/{{([^}]+)}}/g) !== null;
});

const hasVariables = computed(
  () => hasBodyVariables.value || hasTextHeaderVariables.value
);

const renderedHeader = computed(() => {
  return renderTemplatePreview(
    headerText.value,
    processedParams.value.header || {}
  );
});

const renderedTemplate = computed(() => {
  return renderTemplatePreview(
    bodyText.value,
    processedParams.value.body || {}
  );
});

const mediaUploadAccept = computed(
  () => MEDIA_UPLOAD_ACCEPT[headerComponent.value?.format] || ''
);

// Catálogo, detalhes do pedido e similares exigem parâmetros que o seletor
// ainda não coleta; bloqueia o envio em vez de mandar um payload inválido.
const isSendableTemplateType = computed(() =>
  isTemplateTypeSendable(props.template)
);

// Completeness validation is shared with the mobile app via @chatwoot/utils.
const isFormInvalid = computed(
  () =>
    mediaUploading.value ||
    !isSendableTemplateType.value ||
    !isWhatsAppComplete(props.template, processedParams.value)
);

const v$ = useVuelidate(
  {
    processedParams: {
      requiredIfKeysPresent: requiredIf(hasVariables),
      allKeysRequired,
      supportedTemplateType: () => isSendableTemplateType.value,
    },
  },
  { processedParams }
);

const initializeTemplateParameters = () => {
  processedParams.value = buildTemplateParameters(
    props.template,
    hasMediaHeader.value
  );

  if (processedParams.value.body?.nome === '') {
    processedParams.value.body.nome = '{{ contact.name }}';
  }

  applyAutomaticTemplateParameters(
    processedParams.value,
    props.systemVariables
  );
};

const updateMediaUrl = value => {
  processedParams.value.header ??= {};
  processedParams.value.header.media_url = value;
};

const onMediaUrlInput = value => {
  savedMediaRequestId += 1;
  reusedMediaFileName.value = '';
  updateMediaUrl(value);
};

const updateMediaName = value => {
  processedParams.value.header ??= {};
  processedParams.value.header.media_name = value;
};

// Invalidates any pending upload so a late response never fills a newer form.
const discardMediaUpload = () => {
  mediaUploadRequestId += 1;
  mediaUploading.value = false;
  mediaUploadError.value = '';
};

const openMediaPicker = () => {
  mediaFileInput.value?.click();
};

// Prefills the header with the file already hosted for this template, so it is not uploaded again.
const loadSavedHeaderMedia = async () => {
  savedMediaRequestId += 1;
  const requestId = savedMediaRequestId;
  reusedMediaFileName.value = '';
  const format = headerComponent.value?.format;
  if (!hasMediaHeader.value || !props.template?.name || !format) return;

  try {
    const { data } = await TemplateHeaderMediaAPI.savedAsset({
      templateName: props.template.name,
      language: props.template.language,
      headerFormat: format,
    });
    const asset = data?.asset;
    if (requestId !== savedMediaRequestId || !asset?.url) return;
    // Never overwrite a link the agent typed or uploaded meanwhile.
    if (processedParams.value.header?.media_url) return;
    updateMediaUrl(asset.url);
    reusedMediaFileName.value = asset.file_name || asset.url;
    if (isDocumentTemplate.value && !processedParams.value.header?.media_name) {
      updateMediaName(asset.file_name || '');
    }
  } catch {
    // Reuse is optional: the agent can still upload or paste a link.
  }
};

const uploadHeaderMedia = async event => {
  const input = event.target;
  const file = input.files?.[0];
  input.value = '';
  if (!file) return;

  discardMediaUpload();
  const requestId = mediaUploadRequestId;
  mediaUploading.value = true;
  try {
    const { data } = await TemplateHeaderMediaAPI.upload(
      file,
      headerComponent.value?.format,
      { templateName: props.template?.name, language: props.template?.language }
    );
    if (requestId !== mediaUploadRequestId) return;
    savedMediaRequestId += 1;
    reusedMediaFileName.value = '';
    updateMediaUrl(data.url);
    if (isDocumentTemplate.value && !processedParams.value.header?.media_name) {
      updateMediaName(file.name);
    }
  } catch (error) {
    if (requestId !== mediaUploadRequestId) return;
    mediaUploadError.value =
      error?.response?.data?.error ||
      t('WHATSAPP_TEMPLATES.PARSER.MEDIA_UPLOAD_ERROR');
  } finally {
    if (requestId === mediaUploadRequestId) mediaUploading.value = false;
  }
};

const sendMessage = () => {
  v$.value.$touch();
  if (
    v$.value.$invalid ||
    mediaUploading.value ||
    !isSendableTemplateType.value
  )
    return;

  const { name, category, language, namespace } = props.template;

  const payload = {
    message: props.sendRenderedContent
      ? renderedTemplate.value
      : bodyText.value,
    pendingMessageContent: renderedTemplate.value,
    templateParams: {
      name,
      category,
      language,
      namespace,
      parameter_format: props.template.parameter_format || 'POSITIONAL',
      components: props.template.components || [],
      processed_params: processedParams.value,
      // Informa ao backend se o corpo ainda tem os marcadores do modelo, para
      // ele renderizar com os parâmetros e preservar o original na auditoria.
      content_mode: props.sendRenderedContent ? 'rendered' : 'raw_template',
    },
  };
  emit('sendMessage', payload);
};

const resetTemplate = () => {
  emit('resetTemplate');
};

const goBack = () => {
  emit('back');
};

onMounted(() => {
  initializeTemplateParameters();
  loadSavedHeaderMedia();
  document.addEventListener('click', closeVarMenus);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', closeVarMenus);
  discardMediaUpload();
});

watch(
  () => props.template,
  () => {
    discardMediaUpload();
    initializeTemplateParameters();
    loadSavedHeaderMedia();
    v$.value.$reset();
  },
  { deep: true }
);

defineExpose({
  processedParams,
  hasVariables,
  hasMediaHeader,
  isDocumentTemplate,
  headerComponent,
  renderedHeader,
  renderedTemplate,
  isFormInvalid,
  isSendableTemplateType,
  v$,
  updateMediaUrl,
  updateMediaName,
  uploadHeaderMedia,
  loadSavedHeaderMedia,
  reusedMediaFileName,
  mediaUploading,
  mediaUploadError,
  sendMessage,
  resetTemplate,
  goBack,
});
</script>

<template>
  <div>
    <div class="flex flex-col gap-4 p-4 mb-4 rounded-lg bg-n-alpha-black2">
      <div class="flex justify-between items-center">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ template.name }}
        </h3>
        <span class="text-xs text-n-slate-11">
          {{ languageLabel }}
        </span>
      </div>

      <div class="flex flex-col gap-2">
        <div class="rounded-md">
          <div
            v-if="renderedHeader"
            class="mb-2 text-sm font-medium whitespace-pre-wrap text-n-slate-12"
          >
            {{ renderedHeader }}
          </div>
          <div class="text-sm whitespace-pre-wrap text-n-slate-12">
            {{ renderedTemplate }}
          </div>
        </div>
      </div>

      <div class="text-xs text-n-slate-11">
        {{ categoryLabel }}
      </div>
    </div>

    <p
      v-if="!isSendableTemplateType"
      class="flex items-start gap-2 p-3 mb-4 text-sm rounded-lg bg-n-amber-2 text-n-amber-11 outline outline-1 outline-n-amber-5"
      role="alert"
      data-test-id="template-type-unsupported"
    >
      <span
        class="i-hugeicons-alert-circle mt-0.5 size-4 shrink-0"
        aria-hidden="true"
      />
      <span>{{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.SEND_UNSUPPORTED') }}</span>
    </p>

    <div v-if="hasVariables || hasMediaHeader">
      <div v-if="hasMediaHeader" class="mb-4">
        <p class="mb-2.5 text-sm font-semibold">
          {{
            $t('WHATSAPP_TEMPLATES.PARSER.MEDIA_HEADER_LABEL', {
              type: formatType,
            }) || `${formatType} Header`
          }}
        </p>
        <div class="flex flex-wrap items-center gap-2 mb-2.5">
          <Input
            :model-value="processedParams.header?.media_url || ''"
            type="url"
            class="flex-1 min-w-0 basis-60"
            :disabled="mediaUploading"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.MEDIA_URL_LABEL', {
                type: formatType,
              })
            "
            @update:model-value="onMediaUrlInput"
          />
          <input
            ref="mediaFileInput"
            type="file"
            class="hidden"
            :accept="mediaUploadAccept"
            data-test-id="template-header-media-input"
            @change="uploadHeaderMedia"
          />
          <Button
            type="button"
            faded
            slate
            icon="i-hugeicons-upload-04"
            :label="t('WHATSAPP_TEMPLATES.PARSER.MEDIA_UPLOAD')"
            :is-loading="mediaUploading"
            :disabled="mediaUploading"
            @click="openMediaPicker"
          />
        </div>
        <p
          v-if="mediaUploadError"
          class="mb-2.5 text-xs text-n-ruby-11"
          role="alert"
        >
          {{ mediaUploadError }}
        </p>
        <p
          v-else-if="reusedMediaFileName"
          class="mb-2.5 flex items-start gap-1.5 text-xs text-n-teal-11"
          role="status"
        >
          <span
            class="i-hugeicons-checkmark-circle-02 mt-px size-3.5 shrink-0"
            aria-hidden="true"
          />
          <span>{{ t('WHATSAPP_TEMPLATES.PARSER.MEDIA_REUSED_HINT') }}</span>
        </p>
        <p v-else class="mb-2.5 text-xs text-n-slate-11">
          {{ t('WHATSAPP_TEMPLATES.PARSER.MEDIA_UPLOAD_HINT') }}
        </p>
        <div v-if="isDocumentTemplate" class="flex items-center mb-2.5">
          <Input
            :model-value="processedParams.header?.media_name || ''"
            type="text"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.DOCUMENT_NAME_PLACEHOLDER')
            "
            @update:model-value="updateMediaName"
          />
        </div>
      </div>

      <!-- Text Header Variables Section -->
      <div v-if="hasTextHeaderVariables && processedParams.header">
        <p class="mb-2.5 text-sm font-semibold">
          {{ $t('WHATSAPP_TEMPLATES.PARSER.HEADER_VARIABLES_LABEL') }}
        </p>
        <div
          v-for="(variable, key) in processedParams.header"
          :key="`header-${key}`"
          class="flex items-center mb-2.5"
        >
          <Input
            v-model="processedParams.header[key]"
            type="text"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.VARIABLE_PLACEHOLDER', {
                variable: key,
              })
            "
          />
        </div>
      </div>

      <!-- Body Variables Section -->
      <div v-if="processedParams.body">
        <p class="mb-2.5 text-sm font-semibold">
          {{ $t('WHATSAPP_TEMPLATES.PARSER.VARIABLES_LABEL') }}
        </p>
        <div
          v-for="(variable, key) in processedParams.body"
          :key="`body-${key}`"
          class="relative flex items-center gap-1.5 mb-2.5"
        >
          <Input
            v-model="processedParams.body[key]"
            type="text"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.VARIABLE_PLACEHOLDER', {
                variable: key,
              })
            "
          />
          <div v-if="systemVariables.length" class="relative">
            <button
              type="button"
              class="flex items-center gap-1 rounded-md border border-n-weak bg-n-solid-2 px-2 py-1.5 text-xs font-medium text-n-slate-11 hover:bg-n-solid-3 hover:text-n-slate-12 transition-colors"
              @click.stop="toggleVarMenu(key)"
            >
              <span class="i-lucide-braces size-3.5" />
              {{ t('WHATSAPP_TEMPLATES.PARSER.VARIABLE_BUTTON') }}
            </button>
            <div
              v-if="openVarMenu === key"
              class="absolute right-0 z-50 mt-1 min-w-48 rounded-lg border border-n-weak bg-n-solid-1 shadow-lg py-1"
            >
              <button
                v-for="sv in systemVariables"
                :key="sv.value"
                type="button"
                class="w-full px-3 py-2 text-left text-xs hover:bg-n-alpha-2 transition-colors"
                @click="insertSystemVariable(key, sv.value)"
              >
                <span class="block font-medium text-n-slate-12">{{
                  sv.label
                }}</span>
                <span class="block text-n-slate-11 font-mono">{{
                  sv.hint || sv.value
                }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Button Variables Section -->
      <div v-if="processedParams.buttons">
        <p class="mb-2.5 text-sm font-semibold">
          {{ t('WHATSAPP_TEMPLATES.PARSER.BUTTON_PARAMETERS') }}
        </p>
        <div
          v-for="(button, index) in processedParams.buttons"
          :key="`button-${index}`"
          class="flex items-center mb-2.5"
        >
          <Input
            v-model="processedParams.buttons[index].parameter"
            type="text"
            class="flex-1"
            :placeholder="t('WHATSAPP_TEMPLATES.PARSER.BUTTON_PARAMETER')"
          />
        </div>
      </div>
      <p
        v-if="v$.$dirty && v$.$invalid"
        class="p-2.5 text-center rounded-md bg-n-ruby-9/20 text-n-ruby-9"
      >
        {{ $t('WHATSAPP_TEMPLATES.PARSER.FORM_ERROR_MESSAGE') }}
      </p>
    </div>

    <slot
      name="actions"
      :send-message="sendMessage"
      :reset-template="resetTemplate"
      :go-back="goBack"
      :is-valid="!v$.$invalid"
      :disabled="isFormInvalid"
    />
  </div>
</template>
